import { deleteRecipe, searchRecipeList } from '../api/index.js';
import { RecipeCreationDialog } from './RecipeCreationDialog.js';
import { RecipeDeleteDialog } from './RecipeDeleteDialog.js';
import { SearchSection } from './SearchSection.js';
import { GraphVisualizer } from '../graph_visualizer/GraphVisualizer.js';
import { GraphListToggle, type GraphListView } from '../graph_visualizer/GraphListToggle.js';
import type { TabId } from '../graph_visualizer/TabSelector.js';
import { ListView } from '../list_view/ListView.js';
import { TimelineView } from '../timeline_view/TimelineView.js';
import { IngredientsView, PreparationView } from '../recipe_panels/index.js';
import { unitCache } from '../unit-cache.js';
import { RecipeStoreImpl } from '../store/RecipeStore.js';

/** Recipes fetched per page — both the initial browse load and every
 *  subsequent "load more" scroll trigger, filtered or not (see
 *  handleFilterChange()/handleLoadMore()). */
const RECIPE_PAGE_SIZE = 20;

/**
 * Snapshot of "what the user was looking at" on a recipe/home page —
 * kept fresh in the current history entry's state (history.replaceState,
 * see syncHistoryState()) so browser back/forward can restore it via
 * popstate's event.state, instead of every navigation always landing back
 * on the default Graph tab with nothing selected. A *fresh* navigation to
 * the same page (not via back/forward) never carries this — pushState for
 * a new entry always starts with no state — so this only ever kicks in
 * for genuine back/forward.
 */
export interface HomeViewState {
  tab: TabId;
  selectedNodeId: string | null;
}

/**
 * Home page — graph-based layout with GraphVisualizer and side panel.
 *
 * Replaces the old card-grid + HomeGraph + HomeRecipeCard architecture with
 * a single unified GraphVisualizer that renders all root nodes as graph nodes.
 * The side panel is built into the GraphVisualizer's NodeVisualizer.
 */
export class HomePage {
  private container: HTMLElement;
  private searchSection: SearchSection;
  private graphArea: HTMLElement | null = null;
  private sidePanel: HTMLElement | null = null;
  private visualizer: GraphVisualizer | null = null;
  /** RecipeStore - single source of truth for state */
  private store: RecipeStoreImpl;
  /** The raw backend offset to request next, for the CURRENT filter
   *  context — reset to 0 on every fresh search (initial load or a
   *  filter change) and advanced by RECIPE_PAGE_SIZE after every fetch,
   *  successful or not, REGARDLESS of how many valid RecipeListItems it
   *  actually yielded. Deliberately NOT derived from "how many items
   *  are loaded so far": the backend silently drops a recipe with no
   *  root node from its results (see RecipeListItem.from_dict()), so a
   *  page can come back short of RECIPE_PAGE_SIZE while the backend's
   *  own raw ordering has still advanced by the full page size — using
   *  the loaded COUNT as the next offset would re-request (and permanently
   *  skip) whatever came after the dropped recipe. */
  private nextRecipeOffset = 0;
  /** Container for recipe header strip */
  private headerStripContainer: HTMLElement | null = null;
  /** Search section container (shown/hidden on focus) */
  private searchContainer: HTMLElement | null = null;
  /** Search row wrapper (includes refresh button) */
  private searchRow: HTMLElement | null = null;
  /** Current ingredient filter IDs (for refresh) */
  private _currentIngredientIds: string[] = [];
  /** Current tag filter IDs (for refresh) — see _currentIngredientIds. */
  private _currentTagIds: string[] = [];
  /** Recipe ID to auto-focus after initial load (from URL) */
  private _focusRecipeId: string | null = null;
  /** Tab/selection to restore after initial load — only ever set on
   *  browser back/forward (see HomeViewState's own doc comment). */
  private _restoreState: HomeViewState | null = null;
  /** Last selected-node id seen by the store subscription below, to
   *  detect a genuine selection change rather than syncing on every
   *  store notify (see render()). */
  private _lastSyncedSelectedNodeId: string | null = null;

  /** Wide-screen panel containers */
  private ingredientsPanelWide: HTMLElement | null = null;
  private toolsPanelWide: HTMLElement | null = null;
  private preparationPanelWide: HTMLElement | null = null;
  /** Narrow-screen panel containers (in tab wrappers) */
  private ingredientsPanelNarrow: HTMLElement | null = null;
  private toolsPanelNarrow: HTMLElement | null = null;
  private preparationPanelNarrow: HTMLElement | null = null;
  /** List view container and instance */
  private listArea: HTMLElement | null = null;
  /** Narrow-screen frame listArea moves back inside — see syncHomeLayout(). */
  private listAreaFrame: HTMLElement | null = null;
  private listView: ListView | null = null;
  /** Timeline view container and instance — same narrow/wide move pattern as listArea. */
  private timelineArea: HTMLElement | null = null;
  private timelineAreaFrame: HTMLElement | null = null;
  private timelineView: TimelineView | null = null;
  /** Standalone ingredients/tools/preparation views — see recipe_panels/
   *  (tools reuses IngredientsView itself — same plain-row-list shape, no
   *  reason for a parallel class just for a different buildHtml function). */
  private ingredientsView: IngredientsView | null = null;
  private toolsView: IngredientsView | null = null;
  private preparationView: PreparationView | null = null;
  /** Wide-screen graph/list toggle and the list's wide-screen container */
  private graphListToggle: GraphListToggle | null = null;
  private listAreaWide: HTMLElement | null = null;
  private timelineAreaWide: HTMLElement | null = null;

  /**
   * The window resize listener and side-panel MutationObserver render()
   * sets up — stored so destroy() can remove them. Without this (the
   * original bug), every navigation to '/' or '/recipe/:id' created a new
   * HomePage with its own resize listener/observer, and neither was ever
   * torn down: main.ts destroys the GraphVisualizer on navigation, but had
   * no equivalent hook for HomePage itself. Every past instance's resize
   * handler stayed attached to `window` forever, still closing over that
   * instance's own (by-then-detached) DOM nodes. syncHomeLayout() partly
   * looks nodes up by id via document.getElementById() rather than
   * scoping to `this.container` — ids are reused across every render(), so
   * a stale instance's handler firing (on iPhone Safari, a `resize` fires
   * very often — the address bar hiding/showing on scroll, the keyboard
   * opening) would find the *current* page's same-id containers and
   * reattach that old instance's own list/graph elements into them,
   * producing two coexisting list views (old + current) after enough
   * navigations. See destroy(), called from main.ts on every navigation.
   */
  private _resizeHandler: (() => void) | null = null;
  private _layoutObserver: MutationObserver | null = null;

  constructor(container: HTMLElement) {
    this.container = container;
    this.store = new RecipeStoreImpl();
    this.searchSection = new SearchSection((selectedIngredients, selectedTags) => {
      const ingredientIds = Array.from(selectedIngredients.keys());
      const tagIds = Array.from(selectedTags.keys());
      this.handleFilterChange(ingredientIds, tagIds);
    });
  }

  /**
   * Render the full home page UI.
   */
  async render(focusRecipeId?: string, restoreState?: HomeViewState): Promise<void> {
    this._focusRecipeId = focusRecipeId ?? null;
    this._restoreState = restoreState ?? null;
    const PANEL_CLASSES = 'border border-[var(--accent-alpha-20)] rounded-lg p-4 bg-[#1a1a2e]';
    // min-w-0/min-h-0: without it, a flex item's default min-size is its
    // content's natural size, which can force the flex container (and on
    // narrow screens, the whole page) to grow past the space actually
    // available instead of the item shrinking to fit and scrolling
    // internally. Harmless on wide instances, which pin an explicit
    // height via WIDE_HEIGHT_STYLE below anyway (inline style always
    // wins over a class).
    const GRAPH_CLASSES = 'flex-1 min-w-0 min-h-0 border-2 border-[var(--accent-alpha-20)] rounded-lg';
    // Narrow instances rely on the .narrow-fill-page flex layout (see
    // input.css) to size themselves — no fixed height of their own, so
    // they fill exactly the space left after the chrome above them and
    // never force the page itself to scroll. Wide instances still get an
    // explicit height (WIDE_HEIGHT_STYLE, appended where used below);
    // wide screens are unaffected by this change.
    const GRAPH_STYLE = 'position:relative;overflow:hidden;';
    const WIDE_HEIGHT_STYLE = 'height:500px;min-height:500px;';
    const SIDE_PANEL_CLASSES = 'w-full lg:w-96 min-w-0';
    // Only ever used on wide screens now (see the removed narrow
    // this.sidePanel mount below) — always safe to keep a fixed height.
    const SIDE_PANEL_STYLE = 'height:500px;overflow-y:auto;';

    const page = document.createElement('div');
    page.className = 'py-4 max-w-full home-page';

    // Search section — full width, with refresh button
    this.searchRow = document.createElement('div');
    this.searchRow.className = 'search-row flex items-center gap-2 mb-4 px-4';

    this.searchContainer = document.createElement('div');
    this.searchContainer.className = 'flex-1';
    this.searchRow.appendChild(this.searchContainer);
    this.searchSection.render(this.searchContainer);

    const refreshBtn = document.createElement('button');
    refreshBtn.className =
      'px-3 py-2.5 text-sm font-bold font-mono rounded-md border border-[var(--accent-alpha-40)] ' +
      'text-[var(--accent)] hover:bg-[var(--accent-alpha-13)] focus:outline-none focus:ring-2 ' +
      'focus:ring-[var(--accent)] transition-colors flex-shrink-0';
    refreshBtn.textContent = '↻';
    refreshBtn.title = 'Refresca els resultats';
    refreshBtn.addEventListener('click', () => {
      this.handleFilterChange(this._currentIngredientIds, this._currentTagIds);
    });
    this.searchRow.appendChild(refreshBtn);

    page.appendChild(this.searchRow);

    // Recipe header strip container. The edit toolbar mounts itself
    // inside this same bordered box, below the title row (see
    // GraphVisualizer.setEditToolbarContainer).
    this.headerStripContainer = document.createElement('div');
    this.headerStripContainer.id = 'home-recipe-header-strip';
    this.headerStripContainer.className = 'recipe-header-strip-container px-4';
    page.appendChild(this.headerStripContainer);

    // ── Tab selector (narrow only) ──
    const tabSelectorContainer = document.createElement('div');
    tabSelectorContainer.id = 'home-tab-selector';
    tabSelectorContainer.className = 'px-4';
    page.appendChild(tabSelectorContainer);

    // ── NARROW SCREEN: Graph tab (tab-content wrapper) ──
    const tabGraph = document.createElement('div');
    tabGraph.id = 'tab-graph';
    tabGraph.className = 'tab-content flex flex-col gap-4 px-4 lg:hidden';

    this.graphArea = document.createElement('div');
    this.graphArea.id = 'home-graph';
    this.graphArea.className = GRAPH_CLASSES;
    this.graphArea.style.cssText = GRAPH_STYLE;
    tabGraph.appendChild(this.graphArea);

    // this.sidePanel (node detail) is created but no longer mounted here —
    // narrow screens show no side panel at all, only the single active tab
    // filling all available height (see the .narrow-fill-page CSS block in
    // input.css). It's still created unconditionally since GraphVisualizer
    // needs a container reference, and it's reused as the wide-screen node
    // detail panel — see syncHomeLayout(), which moves it into
    // #home-side-panel-wide on wide screens only.
    this.sidePanel = document.createElement('div');
    this.sidePanel.id = 'home-side-panel';
    this.sidePanel.className = SIDE_PANEL_CLASSES;
    this.sidePanel.style.cssText = SIDE_PANEL_STYLE;

    page.appendChild(tabGraph);

    // ── NARROW SCREEN: List tab (tab-content wrapper) ──
    const tabList = document.createElement('div');
    tabList.id = 'tab-list';
    // The frame (thin border, matching PANEL_CLASSES' own convention
    // elsewhere) lives here rather than on listArea itself — listArea
    // also gets moved as-is into listAreaWide on wide screens (below),
    // which already has its own frame; giving it one of its own too would
    // double it up.
    tabList.className = 'tab-content hidden flex flex-col gap-4 px-4 lg:hidden';

    this.listAreaFrame = document.createElement('div');
    // Also a flex *container* (flex flex-col), not just a flex item — it
    // needs to be, so its own flex-1/min-h-0 bounded height actually
    // passes down to listArea's matching classes below. Without that,
    // listArea (no longer a direct child of the flex-col tabList) sizes
    // to its natural content height instead of being height-constrained,
    // and overflow-hidden here just silently clips the overflow instead
    // of it being reachable via listArea's own overflow-y:auto scroll.
    this.listAreaFrame.className = 'flex-1 min-w-0 min-h-0 flex flex-col border border-[var(--accent-alpha-20)] rounded-lg overflow-hidden';
    tabList.appendChild(this.listAreaFrame);

    this.listArea = document.createElement('div');
    this.listArea.id = 'home-list';
    this.listArea.className = 'flex-1 min-w-0 min-h-0';
    this.listArea.style.cssText = 'position:relative;overflow-y:auto;';
    this.listAreaFrame.appendChild(this.listArea);

    page.appendChild(tabList);

    // ── NARROW SCREEN: Elaboració tab (tab-content wrapper) — a single
    // panel with the ingredients list and preparation steps as two
    // sections, rather than the two separate tabs this used to be. ──
    const tabElaboracio = document.createElement('div');
    tabElaboracio.id = 'tab-elaboracio';
    tabElaboracio.className = 'tab-content hidden px-4 lg:hidden';

    const elaboracioPanel = document.createElement('div');
    elaboracioPanel.id = 'home-elaboracio-panel';
    elaboracioPanel.className = PANEL_CLASSES + ' space-y-4';
    tabElaboracio.appendChild(elaboracioPanel);

    this.ingredientsPanelNarrow = document.createElement('div');
    this.ingredientsPanelNarrow.id = 'ingredients-panel';
    elaboracioPanel.appendChild(this.ingredientsPanelNarrow);

    this.toolsPanelNarrow = document.createElement('div');
    this.toolsPanelNarrow.id = 'tools-panel';
    elaboracioPanel.appendChild(this.toolsPanelNarrow);

    this.preparationPanelNarrow = document.createElement('div');
    this.preparationPanelNarrow.id = 'preparation-panel';
    elaboracioPanel.appendChild(this.preparationPanelNarrow);

    page.appendChild(tabElaboracio);

    // ── NARROW SCREEN: Timeline tab (tab-content wrapper) ──
    const tabTimeline = document.createElement('div');
    tabTimeline.id = 'tab-timeline';
    tabTimeline.className = 'tab-content hidden flex flex-col gap-4 px-4 lg:hidden';

    this.timelineAreaFrame = document.createElement('div');
    this.timelineAreaFrame.className = 'flex-1 min-w-0 min-h-0 flex flex-col border border-[var(--accent-alpha-20)] rounded-lg overflow-hidden';
    tabTimeline.appendChild(this.timelineAreaFrame);

    this.timelineArea = document.createElement('div');
    this.timelineArea.id = 'home-timeline';
    this.timelineArea.className = 'flex-1 min-w-0 min-h-0';
    this.timelineArea.style.cssText = 'position:relative;overflow:hidden;';
    this.timelineAreaFrame.appendChild(this.timelineArea);

    page.appendChild(tabTimeline);

    // ── WIDE SCREEN: Graph/List toggle — hidden on narrow (which has its
    // own Graph/List/Ingredients/Preparation tabs via TabSelector) ──
    const graphListToggleContainer = document.createElement('div');
    graphListToggleContainer.id = 'home-graph-list-toggle';
    graphListToggleContainer.className = 'hidden lg:flex gap-2 mb-2 px-4';
    page.appendChild(graphListToggleContainer);

    // ── WIDE SCREEN: Top row — exactly one of Graph / List / Elaboració
    // (plus the side panel, for Graph/List only), switched by
    // graphListToggle below. Elaboració used to sit in an always-visible
    // row underneath instead of being a real third option here — now wide
    // screens show exactly one view at a time, same as narrow ones. ──
    const topRow = document.createElement('div');
    topRow.id = 'home-top-row';
    topRow.className = 'hidden lg:flex flex-row gap-4 mt-2 px-4';

    const graphAreaWide = document.createElement('div');
    graphAreaWide.id = 'home-graph-wide';
    graphAreaWide.className = GRAPH_CLASSES;
    graphAreaWide.style.cssText = GRAPH_STYLE + WIDE_HEIGHT_STYLE;
    topRow.appendChild(graphAreaWide);

    this.listAreaWide = document.createElement('div');
    this.listAreaWide.id = 'home-list-wide';
    // 'flex flex-col' (as a class, not inline style, so it survives
    // syncHomeLayout()'s style.display toggling below) — without it,
    // this.listArea's own 'flex-1 min-h-0' classes are inert (they only
    // do anything inside a flex container): this.listArea never actually
    // gets height-constrained, so it just grows to its content's full
    // height instead of clipping/scrolling internally — its own
    // overflow-y:auto never does anything, and the ListView infinite-
    // scroll sentinel (rooted on this.listArea) reads as permanently
    // "visible" regardless of real scroll position, since nothing of
    // its own content ever actually overflows it. Same bug, same fix,
    // as timelineAreaWide below — see its own comment.
    this.listAreaWide.className = GRAPH_CLASSES + ' flex flex-col';
    this.listAreaWide.style.cssText = GRAPH_STYLE + WIDE_HEIGHT_STYLE + 'display:none;overflow-y:auto;';
    topRow.appendChild(this.listAreaWide);

    this.timelineAreaWide = document.createElement('div');
    this.timelineAreaWide.id = 'home-timeline-wide';
    // 'flex flex-col' (as a class, not inline style, so it survives
    // syncHomeLayout()'s style.display toggling below) — without it,
    // this.timelineArea's own 'flex-1 min-h-0' classes are inert (they
    // only do anything inside a flex container), so when moved in here
    // on wide screens it never actually grows to fill this element's
    // fixed height; it just shrinks to its content's natural height
    // instead, which broke TimelineView's bottom-docked selected-node
    // card (position:absolute;bottom:0 then anchors to that short box,
    // not the real 500px one, so the card visually overlapped the rows
    // instead of sitting below them).
    this.timelineAreaWide.className = GRAPH_CLASSES + ' flex flex-col';
    this.timelineAreaWide.style.cssText = GRAPH_STYLE + WIDE_HEIGHT_STYLE + 'display:none;';
    topRow.appendChild(this.timelineAreaWide);

    // Ingredients list + preparation steps as two side-by-side sections
    // (narrow screens keep them stacked — see the tab-elaboracio panel
    // above, which has no lg:flex-row of its own).
    const elaboracioPanelWide = document.createElement('div');
    elaboracioPanelWide.id = 'home-elaboracio-panel-wide';
    elaboracioPanelWide.className = 'flex-1 min-w-0 flex flex-row gap-4 ' + PANEL_CLASSES;
    elaboracioPanelWide.style.cssText = 'min-height:200px;display:none;';
    topRow.appendChild(elaboracioPanelWide);

    // Ingredients and Utensilis share one column (ingredients first, then
    // tools, stacked) — only Preparació gets its own column now.
    const ingredientsToolsColumnWide = document.createElement('div');
    ingredientsToolsColumnWide.id = 'home-ingredients-tools-column-wide';
    ingredientsToolsColumnWide.className = 'flex-1 min-w-0 space-y-4';
    elaboracioPanelWide.appendChild(ingredientsToolsColumnWide);

    this.ingredientsPanelWide = document.createElement('div');
    this.ingredientsPanelWide.id = 'home-ingredients-panel-wide';
    ingredientsToolsColumnWide.appendChild(this.ingredientsPanelWide);

    this.toolsPanelWide = document.createElement('div');
    this.toolsPanelWide.id = 'home-tools-panel-wide';
    ingredientsToolsColumnWide.appendChild(this.toolsPanelWide);

    this.preparationPanelWide = document.createElement('div');
    this.preparationPanelWide.id = 'home-preparation-panel-wide';
    this.preparationPanelWide.className = 'flex-1 min-w-0';
    elaboracioPanelWide.appendChild(this.preparationPanelWide);

    const sidePanelWide = document.createElement('div');
    sidePanelWide.id = 'home-side-panel-wide';
    sidePanelWide.className = 'w-96 min-w-0';
    sidePanelWide.style.cssText = SIDE_PANEL_STYLE;
    topRow.appendChild(sidePanelWide);

    page.appendChild(topRow);

    this.container.appendChild(page);

    // Initialize GraphVisualizer
    await this.initVisualizer(tabSelectorContainer, graphListToggleContainer);
  }

  // ---------------------------------------------------------------------------
  // GraphVisualizer initialization
  // ---------------------------------------------------------------------------

  private async initVisualizer(tabSelectorContainer: HTMLElement, graphListToggleContainer: HTMLElement): Promise<void> {
    if (!this.graphArea || !this.sidePanel) return;

    // Collect narrow-screen panel containers (from tab wrappers)
    const ingContainers: HTMLElement[] = [];
    const toolContainers: HTMLElement[] = [];
    const prepContainers: HTMLElement[] = [];
    if (this.ingredientsPanelNarrow) ingContainers.push(this.ingredientsPanelNarrow);
    if (this.toolsPanelNarrow) toolContainers.push(this.toolsPanelNarrow);
    if (this.preparationPanelNarrow) prepContainers.push(this.preparationPanelNarrow);
    // Collect wide-screen panel containers
    if (this.ingredientsPanelWide) ingContainers.push(this.ingredientsPanelWide);
    if (this.toolsPanelWide) toolContainers.push(this.toolsPanelWide);
    if (this.preparationPanelWide) prepContainers.push(this.preparationPanelWide);

    this.visualizer = new GraphVisualizer(this.graphArea, this.sidePanel, this.store);

    // Initialize ListView if container exists.
    // No onSelectRecipe/onDeselectRecipe wiring needed here for URL sync —
    // that used to push history state directly from these callbacks, which
    // duplicated (and double-pushed) what onFocusChanged below now does
    // correctly for every focus change regardless of which view caused it.
    if (this.listArea) {
      this.listView = new ListView(this.listArea, this.store);
      this.listView.setCallbacks({
        onEditNode: (nodeId: string) => {
          this.visualizer?.requestEditNode(nodeId);
        },
        onDeleteNode: (nodeId: string) => {
          this.visualizer?.requestDeleteNode(nodeId);
        },
        onReorderSiblings: (parentId: string, newOrder: string[]) => {
          this.visualizer?.requestReorderSiblings(parentId, newOrder);
        },
        onReparentNode: (nodeId: string, newParentId: string, insertIndex?: number) => {
          this.visualizer?.requestReparentNode(nodeId, newParentId, insertIndex);
        },
        onOrphanNode: (nodeId: string) => {
          this.visualizer?.requestOrphanNode(nodeId);
        },
        onToggleExpandMainRoot: async (nodeId: string) => {
          await this.visualizer?.toggleExpandNode(nodeId);
        },
        onDeselectRecipe: () => {
          this.visualizer?.deselectRecipe();
        },
        onCompletePickNode: (nodeId: string) => {
          this.visualizer?.completePickNode(nodeId);
        },
        onGoToIngredient: (nodeId: string) => {
          this.visualizer?.requestGoToIngredient(nodeId);
        },
        onElaborationClick: (nodeId: string) => {
          this.visualizer?.requestElaborationAction(nodeId);
        },
        onRemoveElaboration: (nodeId: string) => {
          this.visualizer?.requestRemoveElaboration(nodeId);
        },
        onAddChild: (nodeId: string) => {
          this.visualizer?.requestCreateChildNode(nodeId);
        },
        onAddParent: () => {
          // Operates on the current selection, not a specific node — safe
          // here since this row's button is only shown while it's the
          // sole selection (see ListNodeItem's own "expanded" gate).
          this.visualizer?.requestAddParentToSelectedNodes();
        },
        onTransform: () => {
          void this.visualizer?.requestTransformSelectedNodes();
        },
        onLoadMore: () => {
          void this.handleLoadMore();
        },
      });
    }

    // Initialize TimelineView if container exists — store-driven, no
    // GraphVisualizer dependency or mutating callbacks (see TimelineView.ts).
    if (this.timelineArea) {
      this.timelineView = new TimelineView(this.timelineArea, this.store);
    }

    // Wide-screen Graph/List toggle — swaps which container is visible in
    // the top row next to the side panel. Independent of the narrow-screen
    // TabSelector, which has its own Graph/List/Ingredients/Preparation tabs.
    this.graphListToggle = new GraphListToggle(graphListToggleContainer, 'graph');
    this.graphListToggle.onChange(() => {
      this.syncHomeLayout();
      this.syncHistoryState();
    });

    // Set up recipe header strip container, then mount the edit toolbar
    // (create/edit/save/discard) inside it, below the title row.
    if (this.headerStripContainer) {
      this.visualizer.setRecipeHeaderContainer(this.headerStripContainer);
      this.visualizer.setEditToolbarContainer();
    }

    // Set up the tab selector (Graph/List/Ingredients/Preparation on narrow screens)
    this.visualizer.setTabContainer(tabSelectorContainer);

    // Standalone Ingredients/Preparation views — independent of
    // GraphVisualizer/ListView, own containers + store subscription. Still
    // delegate HTML building to GraphVisualizer (see IngredientsView's doc
    // comment) — everything else (which recipe to show, row hover/click)
    // goes straight to the store.
    if (ingContainers.length > 0) {
      this.ingredientsView = new IngredientsView(
        ingContainers,
        this.store,
        (recipeId) => this.visualizer!.buildIngredientsPanelHtml(recipeId),
        (nodeId) => this.visualizer?.completePickNode(nodeId),
      );
    }
    if (toolContainers.length > 0) {
      this.toolsView = new IngredientsView(
        toolContainers,
        this.store,
        (recipeId) => this.visualizer!.buildToolsPanelHtml(recipeId),
        (nodeId) => this.visualizer?.completePickNode(nodeId),
        (container) => this.visualizer?.wireToolsPanelHiddenToggle(container),
      );
    }
    if (prepContainers.length > 0) {
      this.preparationView = new PreparationView(
        prepContainers,
        this.store,
        (recipeId) => this.visualizer!.buildPreparationPanelHtml(recipeId),
        (nodeId) => this.visualizer?.completePickNode(nodeId),
      );
    }

    // Wire expand callback from side panel "Obre" button
    this.visualizer?.onExpandRequest((nodeId: string) => {
      this.visualizer?.expandNode(nodeId);
    });

    // Wire callback for creating a new recipe via the global (+) button
    this.visualizer?.onCreateNewRecipe(async () => {
      const dialog = new RecipeCreationDialog();
      const created = await dialog.open();
      if (!created || !this.visualizer) return;

      // Get the main root node from the created recipe's graph
      const graph = created.graph;
      if (!graph) return;
      const mainIndex = created.mainRootIndex ?? 0;
      const rootId = graph.rootNodeIds?.[mainIndex];
      if (!rootId) return;
      const mainRootNode = graph.nodes[rootId];
      if (!mainRootNode) return;

      this.visualizer.addNewRecipeToPool(created.id, mainRootNode);
    });

    // Wire navigation callback for root node double-click / recipe open
    this.visualizer?.onNavigateToRecipe((recipeId: string) => {
      this.navigateToRecipe(recipeId);
    });

    // Wire navigation callback for EditToolbar's [⚘] "go to ingredient" button
    this.visualizer?.onNavigateToIngredient((ingredientId: string) => {
      this.navigateToIngredient(ingredientId);
    });

    // Wire edit mode changes to update filter/search state
    this.visualizer?.onEditModeChanged((editing: boolean) => {
      this.handleEditModeChanged(editing);
    });

    // Wire focus change callback: hide search bar and update URL
    this.visualizer?.onFocusChanged((focused: boolean, recipeId?: string) => {
      if (this.searchRow) {
        this.searchRow.style.display = focused ? 'none' : '';
      }
      if (focused && recipeId) {
        history.pushState({}, '', `/recipe/${recipeId}`);
      } else {
        history.pushState({}, '', '/');
        // When folding back to home, re-fetch and display all recipes
        this.handleFilterChange(this._currentIngredientIds, this._currentTagIds);
      }
    });

    // Wire tab change callback to refresh ListView when switching to list tab
    this.visualizer?.onTabChange((tab: string) => {
      if (tab === 'list' && this.listView) {
        this.listView.render();
      }
      if (tab === 'timeline' && this.timelineView) {
        this.timelineView.render();
      }
      // Removed automatic expand logic: switching views should not change fold state
      // User must explicitly expand/collapse nodes

      // Fired on both screen sizes (see GraphVisualizer.switchToTabOrScroll())
      // — on wide screens, this is what actually switches the wide-screen
      // toggle to match (setActive() already no-ops if it's already
      // active, and already triggers syncHomeLayout() via the onChange
      // wired below); a no-op on narrow, where TabSelector already
      // handled itself. Forwarded for every tab, not just Elaboració —
      // switchToTabOrScroll() (e.g. restoring a saved tab on back/
      // forward) needs 'graph'/'list' to actually flip the wide panel
      // too, not just narrow's own tab-content visibility.
      this.graphListToggle?.setActive(tab as GraphListView);
      this.syncHistoryState();
    });

    // Ensure unit cache is loaded
    unitCache.ensureLoaded().catch((error) => {
      console.error('Failed to load unit cache:', error);
    });

    // Load and display root nodes
    try {
      const items = await searchRecipeList({ limit: RECIPE_PAGE_SIZE });
      this.nextRecipeOffset = RECIPE_PAGE_SIZE;
      this.listView?.setLoadMoreStatus(items.length === 0 ? 'exhausted' : 'idle');
      await this.visualizer.addRootNodes(items);

      // Sync responsive layout
      this.syncHomeLayout();

      // If navigating to /recipe/<id>, auto-expand that recipe.
      if (this._focusRecipeId) {
        const pool = this.visualizer!.getNodePool();
        let found = false;
        for (const node of pool.values()) {
          if (node.recipeId === this._focusRecipeId && node.isMainRoot) {
            await this.visualizer!.expandNode(node.id);
            found = true;
            break;
          }
        }
        // The deep-linked recipe isn't among the initial 20 (searchRecipeList
        // above orders by most-recently-updated, so anything older just
        // isn't in the pool at all) — fetch and add it directly instead of
        // silently doing nothing, which used to make a link to any recipe
        // outside that most-recent set look broken.
        if (!found) {
          await this.visualizer!.loadFullRecipe(this._focusRecipeId);
          // loadFullRecipe (RecipeStore.applyLoadedRecipe) selects the
          // recipe but deliberately doesn't focus it — restoreRecipe()
          // shares that same code path and needs the *opposite* (bring a
          // hidden recipe back alongside whatever's already showing,
          // without isolating it) — so focus explicitly here, matching
          // what the "found in the initial 20" branch above already gets
          // for free from expandNode()'s own main-root fast path.
          this.store.focusRecipe(this._focusRecipeId);
        }
      }

      // Restore the tab/selection the user had before navigating away
      // (browser back/forward only — see HomeViewState's doc comment).
      // Node first, then tab: selecting a node also selects its recipe,
      // which is what makes TabSelector.showAllTabs() run (via the store
      // subscription in the GraphVisualizer constructor) — restoring the
      // tab first could silently no-op if it's 'list'/'elaboracio' and
      // that hasn't happened yet.
      if (this._restoreState) {
        if (this._restoreState.selectedNodeId) {
          await this.visualizer!.selectNodeById(this._restoreState.selectedNodeId);
        }
        this.visualizer!.switchToTabOrScroll(this._restoreState.tab);
      }
    } catch (error) {
      console.error('Error loading home graph:', error);
      this.showErrorState('No s\'han pogut carregar les receptes');
    }

    // Sync on selection change
    this.visualizer?.onSelect(() => { this.syncHomeLayout(); });

    // Selection-history syncing lives on the *store's* own subscription,
    // not GraphVisualizer.onSelect() above — ListView selects nodes by
    // calling this.store.selectNodeWithAutoRecipe() directly, bypassing
    // GraphVisualizer.selectNode() (and therefore onSelect()) entirely,
    // so relying on onSelect() alone silently missed every selection made
    // while on the List tab. Subscribing to the store itself catches every
    // path uniformly, current and future. Diffed against the last-seen id
    // (rather than syncing on every notify, which fires far more often —
    // node drags, hovers, simulation ticks) so this only actually writes
    // when the selection genuinely changes.
    this.store.subscribe(() => {
      const currentId = this.store.getSelectedNode()?.id ?? null;
      if (currentId !== this._lastSyncedSelectedNodeId) {
        this._lastSyncedSelectedNodeId = currentId;
        this.syncHistoryState();
      }
    });

    // Sync on side panel mutations
    if (this.sidePanel) {
      this._layoutObserver = new MutationObserver(() => { this.syncHomeLayout(); });
      this._layoutObserver.observe(this.sidePanel, { childList: true, subtree: true, characterData: true });
    }

    // Sync on resize — see destroy(), which removes this; a named,
    // stored handler (not an inline arrow function like before) is what
    // makes that possible.
    this._resizeHandler = () => { this.syncHomeLayout(); };
    window.addEventListener('resize', this._resizeHandler);
  }

  /**
   * Tear down everything render() attached outside this instance's own
   * DOM subtree (which app.innerHTML = '' already clears on navigation) —
   * the window resize listener and the side-panel MutationObserver. Call
   * before discarding a HomePage instance (main.ts does this on every
   * navigation, mirroring how it already destroys the GraphVisualizer).
   * See the _resizeHandler/_layoutObserver fields' doc comment for why
   * skipping this corrupts a *later* page's DOM, not just this one's.
   */
  destroy(): void {
    if (this._resizeHandler) {
      window.removeEventListener('resize', this._resizeHandler);
      this._resizeHandler = null;
    }
    if (this._layoutObserver) {
      this._layoutObserver.disconnect();
      this._layoutObserver = null;
    }
    this.timelineView?.destroy();
    this.timelineView = null;
  }

  /**
   * Keep the current history entry's state fresh with whatever tab/node
   * is currently showing, via history.replaceState — never adds a new
   * entry, just updates the "sticky note" attached to the one already
   * there for this page visit. Read back via popstate's event.state on
   * browser back/forward (see main.ts's dispatchRoute()/HomeViewState),
   * which is what lets render()'s restoreState handling put things back
   * the way they were instead of always defaulting to Graph/nothing
   * selected. Called from every place tab or selection actually changes.
   */
  private syncHistoryState(): void {
    const tab: TabId = this.graphListToggle?.getActive() ?? 'graph';
    const selectedNodeId = this.store.getSelectedNode()?.id ?? null;
    const state: HomeViewState = { tab, selectedNodeId };
    history.replaceState(state, '', window.location.pathname);
  }

  /**
   * Sync responsive layout: moves graph SVG and copies panel content.
   */
  private syncHomeLayout(): void {
    const isWide = window.innerWidth >= 1024;
    const graphSvg = document.querySelector('#home-graph svg');
    const graphWide = document.getElementById('home-graph-wide');
    const graphNarrow = document.getElementById('home-graph');

    if (isWide) {
      if (graphSvg && graphWide && !graphWide.contains(graphSvg)) {
        graphWide.appendChild(graphSvg);
      }
    } else {
      if (graphSvg && graphNarrow && !graphNarrow.contains(graphSvg)) {
        graphNarrow.appendChild(graphSvg);
      }
      // Restore tab visibility based on the TabSelector's active tab
      this.visualizer?.restoreTabVisibility();
    }

    // Move the list container between its narrow-screen tab and the
    // wide-screen toggle's slot — same "move the live element" approach as
    // the graph SVG above, so ListView's own render() (which rebuilds the
    // inside of this same element) keeps working regardless of which
    // parent it's currently mounted under.
    if (this.listArea && this.listAreaWide) {
      if (isWide) {
        if (!this.listAreaWide.contains(this.listArea)) {
          this.listAreaWide.appendChild(this.listArea);
        }
      } else if (this.listAreaFrame && !this.listAreaFrame.contains(this.listArea)) {
        // Back inside its own thin-bordered frame, not directly into the
        // tab — see where listAreaFrame is built, above.
        this.listAreaFrame.appendChild(this.listArea);
      }
    }

    // Move the timeline container the same way, between its narrow-screen
    // tab and the wide-screen toggle's slot.
    if (this.timelineArea && this.timelineAreaWide) {
      if (isWide) {
        if (!this.timelineAreaWide.contains(this.timelineArea)) {
          this.timelineAreaWide.appendChild(this.timelineArea);
        }
      } else if (this.timelineAreaFrame && !this.timelineAreaFrame.contains(this.timelineArea)) {
        this.timelineAreaFrame.appendChild(this.timelineArea);
      }
    }

    // On wide screens, show only the container matching the toggle's
    // active view — all three stay mounted and continuously re-render via
    // the store subscription regardless, so switching is instant. The
    // side panel (selected node's details) is shown alongside all three,
    // Elaboració included — same node-visualizer companion Graph/List
    // already get.
    const elaboracioWide = document.getElementById('home-elaboracio-panel-wide');
    const spWide = document.getElementById('home-side-panel-wide');
    if (isWide && graphWide && this.listAreaWide && elaboracioWide && this.graphListToggle) {
      const activeView = this.graphListToggle.getActive();
      graphWide.style.display = activeView === 'graph' ? '' : 'none';
      this.listAreaWide.style.display = activeView === 'list' ? '' : 'none';
      elaboracioWide.style.display = activeView === 'elaboracio' ? 'flex' : 'none';
      if (this.timelineAreaWide) {
        this.timelineAreaWide.style.display = activeView === 'timeline' ? '' : 'none';
      }
      if (spWide) spWide.style.display = '';
    }

    // Move the SAME side panel element (with event listeners intact)
    // instead of cloning innerHTML (which loses addEventListener handlers).
    // Referencing this.sidePanel directly (rather than looking it up via
    // getElementById) since on narrow screens it's detached from the DOM
    // entirely — narrow screens have no side panel at all (see render()),
    // so there's nowhere to move it back to; it just stays detached until
    // a resize back to wide reattaches it into #home-side-panel-wide.
    if (this.sidePanel && spWide) {
      if (isWide) {
        if (!spWide.contains(this.sidePanel)) {
          spWide.appendChild(this.sidePanel);
        }
      } else if (spWide.contains(this.sidePanel)) {
        spWide.removeChild(this.sidePanel);
      }
    }

  }

  /**
   * Handle edit mode changes — disable search during editing.
   */
  private handleEditModeChanged(editing: boolean): void {
    if (editing) {
      this.searchSection.disable();
    } else {
      this.searchSection.enable();
    }
  }

  // ---------------------------------------------------------------------------
  // Filter handling
  // ---------------------------------------------------------------------------

  private async handleFilterChange(ingredientIds: string[], tagIds: string[] = []): Promise<void> {
    if (!this.visualizer) return;

    this._currentIngredientIds = ingredientIds;
    this._currentTagIds = tagIds;

    if (ingredientIds.length === 0 && tagIds.length === 0) {
      // No filters — restore all nodes (unhide search-hidden recipes)
      // by applying an empty result set (everything matches). Must match
      // the initial browse load's page size — a larger limit here would
      // pull in recipes beyond the home page's normal browse set every
      // time focus is toggled or a recipe is reloaded (both transiently
      // trigger this same restore path via onFocusChanged).
      try {
        const allItems = await searchRecipeList({ limit: RECIPE_PAGE_SIZE });
        this.nextRecipeOffset = RECIPE_PAGE_SIZE;
        this.listView?.setLoadMoreStatus(allItems.length === 0 ? 'exhausted' : 'idle');
        await this.visualizer.applySearchResults(allItems);
      } catch (error) {
        console.error('Failed to restore graph after filter clear:', error);
      }
      return;
    }

    try {
      const results = await searchRecipeList({ ingredientIds, tagIds, limit: RECIPE_PAGE_SIZE });
      this.nextRecipeOffset = RECIPE_PAGE_SIZE;
      this.listView?.setLoadMoreStatus(results.length === 0 ? 'exhausted' : 'idle');
      await this.visualizer.applySearchResults(results);

      // If the selected recipe is no longer in the results, deselect it
      const selectedRecipeId = this.visualizer.getSelectedRecipeId();
      if (selectedRecipeId) {
        const matchingIds = new Set(results.map(r => r.id));
        if (!matchingIds.has(selectedRecipeId)) {
          this.visualizer.deselectRecipe();
        }
      }
    } catch (error) {
      console.error('Failed to update graph filter:', error);
    }
  }

  /**
   * The footer's "load more" button was clicked — fetch the next page
   * for whatever search context is currently active (plain browse or an
   * ingredient/tag filter, same params handleFilterChange() already
   * uses) and append it to the pool. Additive by design: addRootNodes()
   * (unlike applySearchResults()) never clears existing nodes, so this
   * only ever grows what's shown, exactly what "load more" means.
   *
   * this.nextRecipeOffset tracks the backend's own raw offset, advanced
   * by a full RECIPE_PAGE_SIZE on every fetch regardless of how many
   * valid items it actually returned — see its own doc comment for why
   * that must NOT be the loaded item count instead (a recipe with no
   * root node is silently dropped server-side, which can make a page
   * come back short of RECIPE_PAGE_SIZE while the backend's own
   * ordering has still advanced the full page). Correspondingly, only a
   * genuinely EMPTY page — not merely a short one — means there's
   * nothing left to fetch.
   */
  private async handleLoadMore(): Promise<void> {
    if (!this.visualizer || !this.listView) return;
    this.listView.setLoadMoreStatus('loading');
    try {
      const results = await searchRecipeList({
        limit: RECIPE_PAGE_SIZE,
        offset: this.nextRecipeOffset,
        ingredientIds: this._currentIngredientIds,
        tagIds: this._currentTagIds,
      });
      this.nextRecipeOffset += RECIPE_PAGE_SIZE;
      if (results.length > 0) {
        await this.visualizer.addRootNodes(results);
      }
      this.listView.setLoadMoreStatus(results.length === 0 ? 'exhausted' : 'idle');
    } catch (error) {
      console.error('Failed to load more recipes:', error);
      // Back to 'idle' (not stuck 'loading') so the next scroll-into-view
      // can retry, rather than silently dead-ending the list.
      this.listView.setLoadMoreStatus('idle');
    }
  }

  /**
   * Get the GraphVisualizer instance (for wiring from main.ts).
   */
  getVisualizer(): GraphVisualizer | null {
    return this.visualizer;
  }

  /**
   * Initiate recipe deletion.
   */
  async handleDeleteRecipeById(recipeId: string, recipeTitle: string): Promise<void> {
    const dialog = new RecipeDeleteDialog(recipeTitle);
    const confirmed = await dialog.open();

    if (!confirmed) return;

    try {
      await deleteRecipe(recipeId);
      if (this.visualizer) {
        this.visualizer.clearRecipe(recipeId);
      }
    } catch (error) {
      console.error('Failed to delete recipe:', error);
    }
  }

  // ---------------------------------------------------------------------------
  // Navigation
  // ---------------------------------------------------------------------------

  navigateToRecipe(recipeId: string): void {
    history.pushState({}, '', `/recipe/${recipeId}`);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }

  /** Same pattern as navigateToRecipe() — pushState + a synthetic
   *  popstate lets main.ts's existing listener do the actual route
   *  dispatch, without HomePage needing a direct import of it. */
  navigateToIngredient(ingredientId: string): void {
    history.pushState({}, '', `/ingredient/${ingredientId}`);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }

  // ---------------------------------------------------------------------------
  // Error / empty states
  // ---------------------------------------------------------------------------

  private showErrorState(message: string): void {
    if (!this.graphArea) return;
    this.graphArea.innerHTML = '';
    const msg = document.createElement('div');
    msg.className = 'flex items-center justify-center h-full';
    msg.innerHTML = `
      <p class="text-[#a0a0a0] text-lg font-mono text-center px-4">
        ${this.escapeHtml(message)}
      </p>
    `;
    this.graphArea.appendChild(msg);
  }

  private escapeHtml(str: string): string {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}
